import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { FormControl, FormGroup } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatSort } from '@angular/material/sort';
import produce from 'immer';
import {
  map,
  merge,
  EMPTY,
  Subject,
  startWith,
  switchMap,
  catchError,
  throwError,
  debounceTime,
} from 'rxjs';

import { SubSink } from 'subsink';
import { UserSocket } from 'src/app/core';
import { PaymentService } from 'src/app/feature/payment/services';
import { PaymentTransaction } from 'src/app/shared/interfaces/payment/payment-transaction';
import { PaginationResult } from 'src/app/shared';

@Component({
  selector: 'admin-transactions',
  templateUrl: './admin-transactions.component.html',
  styleUrls: ['./admin-transactions.component.sass'],
})
export class AdminTransactionsComponent implements OnInit, OnDestroy {
  @ViewChild(MatDateRangePicker) dateRangePicker!: MatDateRangePicker<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('idSearch') idSearchInput!: MatInput;
  @ViewChild('type') documentTypes!: MatSelect;
  @ViewChild(MatSort) sort!: MatSort;

  @Input() actions: string[] = [];
  @Input() targetUser!: number;

  // ------------------------------------Public properties|

  public transactionsDataSource!: MatTableDataSource<PaymentTransaction>;
  public loading: boolean = true;

  // -------------------------------------Constructors|

  ngOnInit() {
    this.subsink.sink = this.paymentService
      .getTransactions(this.targetUser)
      .subscribe({
        next: (transactions: PaginationResult<PaymentTransaction>) => {
          this.transactionsDataSource = new MatTableDataSource(
            transactions.items
          );
        },
      });
  }

  public startDate: string = '';
  public endDate: string = '';

  range = new FormGroup({
    start: new FormControl(),
    end: new FormControl(),
  });

  public refreshController$ = new Subject();
  public searchController$ = new Subject();

  public isRateLimitReached: boolean = false;
  public isLoadingResult: boolean = false;
  public resultsLength: number = 0;

  public dataSource = new MatTableDataSource<PaymentTransaction>();
  public displayedColumns: string[] = [
    'id',
    'status',
    'createdAt',
    'amount',
    'user',
    'actions',
  ];

  // ------------------------------------------------Private methods|

  private subsink = new SubSink();

  // ------------------------------------------------Constructor|

  constructor(
    private paymentService: PaymentService,
    private userSocket: UserSocket,
    private cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    // The order of below are important
    merge(
      this.paginator.page,
      this.sort.sortChange,
      this.searchController$.pipe(debounceTime(350)),
      this.refreshController$,
      this.documentTypes.valueChange,
      this.userSocket.onConnect$()
    )
      .pipe(
        catchError((err) => {
          this.isLoadingResult = false;
          this.cdr.detectChanges();
          return throwError(err);
        }),
        startWith({}),
        switchMap((value: any, index: any) => {
          this.isLoadingResult = true;
          this.cdr.detectChanges();
          const order = value?.direction
            ? value.direction.toUpperCase()
            : undefined;

          const search = value?.idSearch ? value.idSearch : undefined;

          const page = this.paginator.pageIndex + 1;
          const limit = this.paginator.pageSize;

          return this.paymentService
            .getTransactions(this.targetUser, {
              page,
              limit,
              orderBy: value?.active,
              orderType: value?.direction?.toUpperCase(),
            })
            .pipe(catchError(() => EMPTY));
        }),
        map((data) => {
          this.isLoadingResult = false;
          this.isRateLimitReached = data.items.length === 0;

          if (this.isRateLimitReached) {
            return [];
          }

          this.resultsLength = data.meta.totalItems + 1;
          return data.items;
        })
      )
      .pipe(catchError(() => EMPTY))
      .subscribe(
        (data: any) => (this.dataSource = new MatTableDataSource(data))
      );
  }

  // ---------------------------------------------------------Public methods|

  public getDate(date: string): Date {
    return new Date(date);
  }

  public getTotalCost(): number {
    if (this.dataSource.data.length <= 0) return 0;
    return +this.dataSource.data
      .map((item) => item.amount || 0)
      .reduce((a, b) => (+a + +b) as any);
  }

  public exportExcel() {
    const data = this.prepareDataToExport();

    const xlsx = require('xlsx');
    const worksheet = xlsx.utils.json_to_sheet(data);
    const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = xlsx.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    this.saveAsExcelFile(excelBuffer, 'paid_baskets');
  }

  public prepareDataToExport(): any[] {
    return produce(this.dataSource.data, (draft) => {
      draft.forEach((basket: any) => {
        Object.keys(basket).forEach((key) => {
          if (!basket[key]) {
            basket[key] = '0';
          }
        });
      });
    });
  }

  public saveAsExcelFile(buffer: any, fileName: string): void {
    const FileSaver = require('file-saver');

    let EXCEL_TYPE =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    let EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });

    FileSaver.saveAs(
      data,
      fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION
    );
  }

  ngOnDestroy() {
    this.subsink.unsubscribe();
  }
}
